'use client';

import { Authentication, AuthenticationVariant, Loader, validIcons } from '@brahma/components';
import styles from './login-field.module.scss';
import { useEffect, useState } from 'react';
import { useFetch, useLoadingRouter } from '@brahma/hooks';
import { DATAFLOW_LOGO_URL, PAGE_URLS, SEARCH_PARAMS_KEYS } from '$constants/index';
import useLocale from '$hooks/use-locale';
import { ONBOARDING } from '$locale/constants';
import type { VerifierAuthenticationAction, VerifierAuthenticationState } from '$context/global-context/reducers/types';
import { ContextAction } from '$context/global-context/types';
import useGlobalContext from '$hooks/use-global-context';
import { extractUserData, interpolateString } from '@brahma/utils';
import { useSearchParams } from 'next/navigation';
import { API_URLS } from '$constants/apis';
import type { IBFFResponse, User } from '@dfg/brahma-types';
const LoginField = () => {
  const [email, setEmail] = useState('');
  const {
    routerPush,
    loading
  } = useLoadingRouter();
  const [emailError, setEmailError] = useState(false);
  const {
    t
  } = useLocale(ONBOARDING);
  const {
    state,
    dispatch
  } = useGlobalContext();
  const searchParams = useSearchParams();
  const token = searchParams.get(SEARCH_PARAMS_KEYS.token) as string;
  const code = searchParams.get(SEARCH_PARAMS_KEYS.code) as string;
  const {
    data: validateMagicLinkData,
    fetch: validateMagicLink,
    loading: magicLinkLoading
  } = useFetch<IBFFResponse<{
    userData: User;
  }>>({
    url: interpolateString(API_URLS.validateAutoLogin, {
      token
    }),
    component: 'LoginField'
  }, false);
  useEffect(() => {
    if (token) {
      validateMagicLink();
    }
  }, [token]);
  useEffect(() => {
    if (validateMagicLinkData) {
      const userInfo = validateMagicLinkData?.data?.userData as User;
      const user = userInfo && extractUserData(userInfo);
      dispatch({
        type: ContextAction.setUser,
        payload: user
      });
      routerPush(PAGE_URLS.PendingRequests);
    }
  }, [validateMagicLinkData]);
  useEffect(() => {
    //handle magic code
    if (code) {
      window.location.href = `${process.env.NEXT_PUBLIC_CTA_SERVICE_URL}${API_URLS.validateCtaMagicCode}${code.toUpperCase()}`;
    }
  }, [code]);
  const emailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    if (emailValue.length > 0) {
      setEmailError(false);
    }
    setEmail(emailValue);
  };
  const emailSubmit = () => {
    if (email === '') {
      setEmailError(true);
      return;
    } else {
      const payloadState: VerifierAuthenticationState = {
        email: email
      };
      const payload: VerifierAuthenticationAction = {
        type: ContextAction.setVerifierAuthentication,
        payload: payloadState
      };
      dispatch(payload);
      routerPush(interpolateString(PAGE_URLS.loginOtp, {
        verifierEmail: email
      }));
    }
  };
  return <>
      <Loader show={magicLinkLoading || loading} data-sentry-element="Loader" data-sentry-source-file="index.tsx" />
      <div className={styles.emailFieldContainer}>
        <Authentication title={t('authentication.email')} description={<p className={styles.description}>
              {t('authentication.emailDesc')}
            </p>} headerIcon={validIcons.messageTextSquare} variant={AuthenticationVariant.EMAIL} emailPlaceholder={t('authentication.emailPlaceholder')} emailErrorMsg={emailError ? t('authentication.emailRequiredMsg') : ''} emailLabel={t('authentication.emailLabel')} emailValue={email} emailInputChange={emailChange} actionBtnClick={emailSubmit} actionBtnText={t('authentication.getOtp')} logoUrls={[state?.coBrandDetails?.logo, DATAFLOW_LOGO_URL].filter(Boolean)} data-sentry-element="Authentication" data-sentry-source-file="index.tsx" />
      </div>
    </>;
};
export default LoginField;